import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Filter } from "lucide-react";

import { get } from "@/lib/api-client";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { PaymentDetailModal } from "./PaymentDetailModal";
import type { Payment } from "@/types/payment";

export function PaymentsQueuePage() {
  const [tab, setTab] = useState("pending");
  const [purposeFilter, setPurposeFilter] = useState("all"); // 🛠️ NEW: Filter state
  const [selected, setSelected] = useState<Payment | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-payments", tab],
    queryFn: () => get<Payment[]>("/admin/payments", { decision: tab === "all" ? "" : tab }),
    refetchInterval: 20_000,
  });

  const rows = data ?? [];
  
  // 🛠️ NEW: Apply purpose filter to the rows
  const filteredRows = purposeFilter === "all" 
    ? rows 
    : rows.filter((p) => p.purpose === purposeFilter);

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Payment Verification Queue</h1>
        <p className="text-sm text-surface-500 mt-1">
          Review manual payment submissions. Approvals trigger order completion, inventory commit, and point awards.
        </p>
      </header>

      <Card>
        <CardHeader className="flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pb-0 border-b-0 px-5 pt-4">
          <Tabs 
            value={tab} 
            onChange={setTab}
            tabs={[
              { value: "pending",  label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "resubmission_requested", label: "Resubmission" },
              { value: "all",      label: "All" },
            ]} 
          />
          
          {/* 🛠️ NEW: Purpose Filter Buttons */}
          <div className="flex items-center gap-2 bg-surface-100 p-1 rounded-lg border border-surface-200 self-end sm:self-auto mb-3 sm:mb-0">
            <Filter className="w-3.5 h-3.5 text-surface-400 ml-1.5" />
            <button 
              onClick={() => setPurposeFilter("all")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${purposeFilter === "all" ? "bg-white shadow-sm text-surface-900" : "text-surface-500 hover:text-surface-700"}`}
            >
              All
            </button>
            <button 
              onClick={() => setPurposeFilter("order")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${purposeFilter === "order" ? "bg-white shadow-sm text-surface-900" : "text-surface-500 hover:text-surface-700"}`}
            >
              Shop Orders
            </button>
            <button 
              onClick={() => setPurposeFilter("membership")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${purposeFilter === "membership" ? "bg-white shadow-sm text-surface-900" : "text-surface-500 hover:text-surface-700"}`}
            >
              Point Packages
            </button>
          </div>
        </CardHeader>
        
        <CardBody className="p-0 border-t border-surface-200">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : filteredRows.length === 0 ? (
            <EmptyState icon={CreditCard} title="No payments in this view" description="Try changing the status or purpose filters." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Payment ID</TH>
                  <TH>Customer</TH>
                  <TH>Purpose</TH>
                  <TH>Method</TH>
                  <TH>Sender Account</TH>
                  <TH>Amount</TH>
                  <TH>Ref</TH>
                  <TH>Submitted</TH>
                  <TH>Decision</TH>
                </TR>
              </THead>
              <tbody>
                {filteredRows.map((p) => (
                  <TR key={p.payment_id} onClick={() => setSelected(p)} className="cursor-pointer hover:bg-surface-50">
                    <TD className="font-mono text-xs text-brand-700 font-semibold">{p.payment_id}</TD>
                    <TD className="text-xs">{p.customer_id}</TD>
                    <TD>
                      <Badge tone={p.purpose === "order" ? "info" : "warning"} className="capitalize">
                        {p.purpose === "membership" ? "Package" : p.purpose}
                      </Badge>
                    </TD>
                    <TD className="text-xs font-semibold uppercase tracking-wider text-surface-600">{p.method}</TD>
                    <TD className="font-mono text-xs font-bold text-surface-700">{p.sender_account || "—"}</TD>
                    <TD className="font-bold text-brand-700">{formatCurrency(p.amount)}</TD>
                    <TD className="font-mono text-[10px] text-surface-500">
                      <span className="block max-w-[100px] truncate" title={p.transaction_reference}>
                        {p.transaction_reference}
                      </span>
                    </TD>
                    <TD className="text-[10px] text-surface-500 whitespace-nowrap">{formatDateTime(p.created_at)}</TD>
                    <TD><StatusPill status={p.decision} /></TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {selected && (
        <PaymentDetailModal
          open 
          onClose={() => setSelected(null)}
          payment={selected}
          onAction={() => { refetch(); setSelected(null); }}
        />
      )}
    </div>
  );
}